import { Component } from '@theme/component';

const HEADER_OFFSET = 120; // sticky header + announcement bar

/**
 * Routine quiz component
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} resultBanner
 * @property {HTMLElement} resultHeading
 * @property {HTMLElement} resultNote
 * @property {HTMLAnchorElement} goToRoutine
 * @property {HTMLButtonElement} showAllRoutines
 *
 * @extends {Component<Refs>}
 */
class RoutineQuiz extends Component {
  requiredRefs = ['resultBanner', 'resultHeading', 'resultNote', 'goToRoutine', 'showAllRoutines'];

  /**
   * The goal chosen in step 1.
   *
   * @type {{ id: string, anchor: string, heading: string } | null}
   */
  #goal = null;

  #started = false;

  connectedCallback() {
    super.connectedCallback();
    this.#collectRows();
    this.#followDeepLink();
  }

  #collectRows() {
    const target = this.querySelector('.quiz__rows');
    const options = this.querySelector('.quiz__options');
    if (!target || !options) return;

    for (const row of options.querySelectorAll('[data-quiz-row]')) {
      row.removeAttribute('hidden');
      target.append(row);
    }
  }

  #followDeepLink() {
    const id = location.hash.slice(1);
    if (!id) return;
    const target = this.querySelector(`#${CSS.escape(id)}`);
    if (!target || !target.hasAttribute('data-quiz-routine')) return;
    const run = () => setTimeout(() => this.#scrollTo(target), 250);
    // `load` may already have fired by the time this custom element upgrades (it's defined in a
    // deferred module script), in which case a listener registered here would never run.
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }
  }

  #push(detail) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ rq_source: 'routine_quiz', ...detail });
  }

  /**
   * el.scrollIntoView() looked like the safer option here, but WebKit has a known bug where it
   * can also scroll *nested* scrollable elements as a side effect of bringing the target into
   * view (https://github.com/ionic-team/ionic-framework/pull/28376) — on this page that meant it
   * was nudging each product card's own image carousel to a different slide on iOS Safari,
   * showing the wrong product photo. window.scrollTo() only ever moves the page itself, never a
   * descendant scroll container, so it doesn't have that failure mode.
   *
   * The original bug this replaced was computing the offset via getBoundingClientRect()
   * immediately after hiding the quiz panel, before the browser had necessarily finished
   * re-laying-out the now-shorter page — requestAnimationFrame defers the read to the next paint,
   * once that layout has settled, instead of guessing with a fixed setTimeout delay.
   */
  #scrollTo(el) {
    if (!(el instanceof HTMLElement)) return;
    requestAnimationFrame(() => {
      const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  }

  selectGoal(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    if (!this.#started) {
      this.#started = true;
      this.#push({ event: 'rq_quiz_start' });
    }

    this.#goal = {
      id: button.dataset.goalId ?? '',
      anchor: button.dataset.quizAnchor ?? '',
      heading: button.dataset.resultHeading ?? '',
    };

    this.#push({ event: 'rq_quiz_goal', rq_goal: this.#goal.anchor });
    this.#applyBrushLabel(button.dataset.brushLabel ?? '');

    if (button.dataset.skipStepTwo === 'true') {
      this.#complete('everything');
      return;
    }

    this.#showStep(2);
  }

  back() {
    this.#showStep(1);
  }

  finish(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const startType = button.dataset.startType;
    if (!this.#goal || (startType !== 'brush' && startType !== 'everything')) return;

    this.#complete(startType);
  }

  /**
   * Shared by a normal step-2 pick (`finish`) and a goal configured to skip step 2 entirely
   * (`selectGoal`), which completes straight from the goal button with no start-option button
   * click to read `resultNote` from — so this looks the matching start-option button up itself.
   *
   * @param {'brush' | 'everything'} startType
   */
  #complete(startType) {
    const goal = this.#goal;
    if (!goal) return;

    const startButton = this.querySelector(`[data-start-type="${startType}"]`);
    const resultNote = startButton instanceof HTMLElement ? startButton.dataset.resultNote ?? '' : '';

    this.#push({ event: 'rq_quiz_complete', rq_goal: goal.anchor, rq_start: startType });

    this.refs.resultHeading.textContent = goal.heading;
    this.refs.resultNote.textContent = resultNote;
    this.refs.goToRoutine.setAttribute('href', `#${goal.anchor}`);
    this.refs.resultBanner.hidden = false;

    const panel = this.querySelector('.quiz__panel');
    if (panel instanceof HTMLElement) panel.hidden = true;

    for (const row of this.querySelectorAll('.quiz-row')) {
      row.classList.remove('is-match');
      row.removeAttribute('data-quiz-start');
    }

    const target = goal.anchor ? this.querySelector(`#${CSS.escape(goal.anchor)}`) : null;
    if (target instanceof HTMLElement) {
      target.classList.add('is-match');
      target.setAttribute('data-quiz-start', startType);
    } else {
      console.warn(
        `[routine-quiz] no row with id "${goal.anchor}" — check the goal option's row configuration.`
      );
    }

    this.#applyFocusMode(goal.anchor);

    this.refs.resultHeading.focus({ preventScroll: true });
    setTimeout(() => this.#scrollTo(target), 350);
  }

  /**
   * Step 2's "I have a brush" option is shared across every goal, but its wording doesn't always
   * fit (e.g. "I have a water flosser" for the gum-health goal) — so each goal button can carry
   * an override, applied here when that goal is picked, and reset via the start-option's own
   * `data-default-heading` when the next goal picked has none.
   *
   * @param {string} label
   */
  #applyBrushLabel(label) {
    const brushButton = this.querySelector('[data-start-type="brush"]');
    if (!(brushButton instanceof HTMLElement)) return;

    const headingEl = brushButton.querySelector('.quiz-option__heading');
    if (!(headingEl instanceof HTMLElement)) return;

    headingEl.textContent = label || brushButton.dataset.defaultHeading || headingEl.textContent;
  }

  goToRoutine(event) {
    event.preventDefault();
    if (!this.#goal?.anchor) return;
    this.#scrollTo(this.querySelector(`#${CSS.escape(this.#goal.anchor)}`));
  }

  showAllRoutines() {
    this.#clearFocusMode();
    this.#push({ event: 'rq_quiz_see_all', rq_goal: this.#goal?.anchor ?? '' });
  }

  restart() {
    const panel = this.querySelector('.quiz__panel');
    if (panel instanceof HTMLElement) panel.hidden = false;
    this.refs.resultBanner.hidden = true;

    for (const row of this.querySelectorAll('.quiz-row')) {
      row.classList.remove('is-match');
      row.removeAttribute('data-quiz-start');
    }

    this.#clearFocusMode();
    this.#showStep(1);
    this.#scrollTo(this);
    this.#push({ event: 'rq_quiz_restart' });
  }

  #applyFocusMode(matchedAnchor) {
    if (this.dataset.focusMode !== 'true') return;
    for (const row of this.querySelectorAll('.quiz-row')) {
      row.classList.toggle('quiz-row--dim', row.id !== matchedAnchor);
    }
    this.refs.showAllRoutines.hidden = false;
  }

  #clearFocusMode() {
    for (const row of this.querySelectorAll('.quiz-row--dim')) {
      row.classList.remove('quiz-row--dim');
    }
    this.refs.showAllRoutines.hidden = true;
  }

  #showStep(step) {
    for (const el of this.querySelectorAll('[data-quiz-step]')) {
      if (!(el instanceof HTMLElement)) continue;
      el.toggleAttribute('hidden', el.dataset.quizStep !== String(step));
    }

    for (const dot of this.querySelectorAll('[data-quiz-dot]')) {
      if (!(dot instanceof HTMLElement)) continue;
      dot.classList.toggle('is-active', dot.dataset.quizDot === String(step));
    }

    const heading = this.querySelector(`.quiz__step-header[data-quiz-step="${step}"] .quiz__step-heading`);
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
  }
}

if (!customElements.get('routine-quiz')) {
  customElements.define('routine-quiz', RoutineQuiz);
}